import * as React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  ChevronDown,
  ChevronRight,
  Settings2,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { API } from 'aws-amplify';
import { getWeek } from 'date-fns';
import useCircleLevels from '../../../application/hooks/useCircleLevels';
import useKPIs from '../../../application/hooks/useKPIs';
import useLeaderboardViews from '../../../application/hooks/useLeaderboardViews';
import useLeaderboardStore, {
  DimensionTimeframe,
  DimensionType,
  LeaderboardFilters,
  LeaderboardStackView,
  Result,
} from '../../../application/store/zustand/useLeaderboardStore';
import {
  createNewViewStask,
  getDimensionValue,
  getWeekDimensionFormattedDate,
} from '../../../application/routes/leaderboard/Leaderboard.utils';
import useOrgStore from '../../../application/store/zustand/useOrgStore';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { UserAvatar } from '../../components/ui/avatar';
import { cn } from '../../lib/utils';
import { useToast } from '../../components/ui/toast';

declare const global: {
  me: {
    id: string;
    orgID: string;
  };
};

const GET_KPI_VALUES_QUERY = `
  query GetKPIValuesV6(
    $dimensionType: String!
    $dimensionTimeframe: String!
    $dimensionValue: String!
    $nextToken: String
    $limit: Int
    $sortColumn: String
    $sortOrder: String
    $circleLevelID: String
    $circleID: String
    $viewID: String
    $parentCircleID: String
  ) {
    getKPIValuesV6(
      dimensionType: $dimensionType
      dimensionTimeframe: $dimensionTimeframe
      dimensionValue: $dimensionValue
      nextToken: $nextToken
      limit: $limit
      sortColumn: $sortColumn
      sortOrder: $sortOrder
      circleLevelID: $circleLevelID
      circleID: $circleID
      viewID: $viewID
      parentCircleID: $parentCircleID
    )
  }
`;

const GET_USERS_STATUS_QUERY = `
  query SearchUsers($orgID: ID!, $nextToken: String) {
    searchUsers(
      filter: {
        orgID: { eq: $orgID }
      }
      limit: 1000
      nextToken: $nextToken
    ) {
      items {
        id
        name
        status
      }
      nextToken
    }
  }
`;

const formatDateForWeekInput = (dateStr: string) => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const week = getWeek(date, { weekStartsOn: 1 });
  return `${year}-W${week.toString().padStart(2, '0')}`;
};

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2017; year <= currentYear; year++) {
    years.push(year);
  }
  return years;
};

export function LeaderboardPage() {
  const { circleLevels } = useCircleLevels();
  const { KPIs } = useKPIs();
  const { leaderboardViews, defaultView } = useLeaderboardViews();
  const {
    setViewsStack,
    leaderboardFilters,
    viewsStack,
    updateFilters,
    isLoading,
    leaderboardData,
    applyView,
  } = useLeaderboardStore();
  const { circleLevels: orgCircleLevels } = useOrgStore();
  const navigate = useNavigate();
  const toastCtx = useToast();

  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);

  useEffect(() => {
    const initialAssetsLoaded =
      leaderboardViews && KPIs && circleLevels && circleLevels.length > 0;
    if (!initialAssetsLoaded) {
      return;
    }

    if (defaultView) {
      applyView(defaultView.id);
    } else {
      setViewsStack(createNewViewStask(circleLevels[0]));
    }
  }, [leaderboardViews, KPIs, circleLevels]);

  const handleSort = useCallback(
    (columnId: string) => {
      if (leaderboardFilters?.sortBy === columnId) {
        updateFilters({
          sortOrder: leaderboardFilters?.sortOrder === 'asc' ? 'desc' : 'asc',
        });
      } else {
        updateFilters({
          sortBy: columnId,
          sortOrder: 'desc',
        });
      }
    },
    [leaderboardFilters, updateFilters]
  );

  const handleCircleClick = useCallback(
    (row: any) => {
      const { id, name } = row;
      const currentView = viewsStack[viewsStack.length - 1];

      const currentLevelIndex = circleLevels.findIndex(
        (level) => level.id === currentView.circleLevelID
      );
      const nextView: LeaderboardStackView = {
        viewID: currentView.viewID,
        parentCircleID: id,
        parentCircleName: name,
        circleID: id,
        circleName: name,
      };

      if (currentLevelIndex === circleLevels.length - 1) {
        nextView.dimensionType = 'person';
        nextView.circleLevelID = currentView.circleLevelID;
        nextView.circleLevelName = currentView.circleLevelName;
      } else {
        nextView.dimensionType = 'circle';
        nextView.circleLevelID = circleLevels[currentLevelIndex + 1].id;
        nextView.circleLevelName = circleLevels[currentLevelIndex + 1].name;
      }

      setViewsStack([...viewsStack, nextView]);
    },
    [viewsStack, circleLevels, setViewsStack]
  );

  const handlePersonClick = useCallback(
    (id: string) => {
      navigate(`/v2/people/${id}`, {
        state: { leaderboard: true },
      });
    },
    [navigate]
  );

  const handleLimitChange = useCallback(
    (limit: number) => {
      updateFilters({ limit });
    },
    [updateFilters]
  );

  const handleStackClick = useCallback(
    (index: number) => {
      if (index === viewsStack.length - 1) {
        return;
      }
      setViewsStack(viewsStack.slice(0, index + 1));
    },
    [viewsStack, setViewsStack]
  );

  // CSV Download logic
  const fetchUserStatuses = async (): Promise<{ [userId: string]: string }> => {
    const userStatuses: { [userId: string]: string } = {};
    let nextToken = '';

    do {
      const apiResult = (await API.graphql({
        query: GET_USERS_STATUS_QUERY,
        variables: {
          orgID: (global as any).me.orgID,
          nextToken,
        },
      })) as any;

      const users = apiResult.data.searchUsers.items;
      users.forEach((user: any) => {
        userStatuses[user.id] = user.status;
      });

      nextToken = apiResult.data.searchUsers.nextToken || '';
    } while (nextToken);

    return userStatuses;
  };

  const fetchAllLeaderboardData = async (): Promise<{
    results: Result[];
    kpiList: { [key: string]: any };
  }> => {
    if (!leaderboardFilters) {
      throw new Error('No leaderboard filters available');
    }

    const allResults: Result[] = [];
    let nextToken = '';
    let kpiList: { [key: string]: any } = {};
    let isFirstPage = true;
    let pageCount = 0;

    do {
      pageCount++;
      setDownloadProgress(pageCount);
      const variables = {
        dimensionType: leaderboardFilters.dimensionType,
        dimensionTimeframe: leaderboardFilters.dimensionTimeframe,
        dimensionValue: leaderboardFilters.dimensionValue,
        nextToken,
        limit: 250,
        sortColumn: leaderboardFilters.sortBy,
        sortOrder: leaderboardFilters.sortOrder,
        circleLevelID: leaderboardFilters.circleLevelID,
        circleID: leaderboardFilters.circleID,
        parentCircleID: leaderboardFilters.parentCircleID,
        viewID: leaderboardFilters.viewID,
      };

      const apiResult = (await API.graphql({
        query: GET_KPI_VALUES_QUERY,
        variables,
      })) as any;

      const parsedResult = JSON.parse(apiResult.data.getKPIValuesV6);

      if (isFirstPage) {
        kpiList = parsedResult.kpiList;
        isFirstPage = false;
      }

      allResults.push(...parsedResult.results);
      nextToken = parsedResult.nextToken || '';
    } while (nextToken);

    return { results: allResults, kpiList };
  };

  const handleDownloadCSV = useCallback(async () => {
    if (!leaderboardFilters) {
      toastCtx.error(
        'Error',
        'No leaderboard configuration available. Please try refreshing the page.'
      );
      return;
    }

    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      const userStatuses = await fetchUserStatuses();
      const allData = await fetchAllLeaderboardData();

      if (!allData.results || allData.results.length === 0) {
        toastCtx.error('No Data', 'No data available for export.');
        return;
      }

      const filteredResults = allData.results.filter((result: Result) => {
        const userStatus = userStatuses[result.id];
        return userStatus !== 'deleted';
      });

      if (filteredResults.length === 0) {
        toastCtx.error('No Data', 'No active users found for export.');
        return;
      }

      const csvRows = [];
      const headers = ['Rank', 'Name', ...Object.keys(allData.kpiList || {})];
      csvRows.push(headers.join(','));

      const sortedResults = [...filteredResults].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );

      sortedResults.forEach((row: Result) => {
        const values = [
          row.rank,
          `"${row.name.replace(/"/g, '""')}"`,
          ...Object.keys(allData.kpiList || {}).map((kpiKey) => {
            const value = row.kpis[kpiKey];
            if (value === undefined || value === null) {
              return '""';
            }
            return `"${String(value).replace(/"/g, '""')}"`;
          }),
        ];
        csvRows.push(values.join(','));
      });

      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `Leaderboard_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toastCtx.success(
        'Success',
        `Downloaded ${sortedResults.length} records successfully.`
      );
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toastCtx.error('Error', 'Failed to download CSV. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [leaderboardFilters, toastCtx]);

  // Highlights data
  const highlights = useMemo(() => {
    if (!leaderboardData?.highlightsData) {
      return [];
    }
    return Object.values(leaderboardData.highlightsData) as any[];
  }, [leaderboardData?.highlightsData]);

  // KPI columns
  const kpiColumns = useMemo(() => {
    if (!leaderboardData?.kpiList) {
      return [];
    }
    return Object.keys(leaderboardData.kpiList);
  }, [leaderboardData?.kpiList]);

  // Pagination
  const nextTokensList = leaderboardData?.nextTokensList || [];
  const totalPages = nextTokensList.length + 1;
  const currentPageIndex = ['', ...nextTokensList].indexOf(
    leaderboardFilters?.nextToken || ''
  );
  const currentPage = currentPageIndex + 1;

  const getVisiblePages = useCallback(() => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;
    const sidePages = 1;

    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    pages.push(1);

    let start = Math.max(2, currentPage - sidePages);
    let end = Math.min(totalPages - 1, currentPage + sidePages);

    if (currentPage <= sidePages + 2) {
      end = Math.min(totalPages - 1, maxVisiblePages - 1);
    }

    if (currentPage >= totalPages - sidePages - 1) {
      start = Math.max(2, totalPages - maxVisiblePages + 1);
    }

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages - 1) {
      pages.push('...');
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }, [totalPages, currentPage]);

  const handlePageChange = useCallback(
    (page: number | string) => {
      if (typeof page === 'number') {
        const newIndex = page - 1;
        const newToken = ['', ...nextTokensList][newIndex];
        updateFilters({ nextToken: newToken });
      }
    },
    [nextTokensList, updateFilters]
  );

  // Loading state
  if (
    !(leaderboardViews && KPIs && circleLevels && circleLevels.length > 0) ||
    !leaderboardData
  ) {
    return (
      <div className="flex flex-1 items-center justify-center h-full">
        <Loader2 className="size-10 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <PageHeader
        title="Leaderboard"
        actions={[
          {
            id: 'download-csv',
            label: isDownloading
              ? downloadProgress > 0
                ? `Page ${downloadProgress}...`
                : 'Downloading...'
              : 'Download CSV',
            icon: isDownloading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            ),
            variant: 'primary',
            onClick: handleDownloadCSV,
          },
        ]}
      />

      <div className="flex-1 overflow-auto min-w-0">
        {/* Highlights Cards */}
        {highlights.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 lg:px-10 py-4">
            {highlights.slice(0, 4).map((highlight: any) => (
              <div
                key={highlight.id}
                className="flex items-center justify-between p-4 border border-border rounded-xl bg-background"
              >
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    {highlight.title}
                  </span>
                  <span className="text-lg font-semibold text-foreground">
                    {highlight.total}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-3 px-4 sm:px-6 lg:px-10 py-4 overflow-hidden">
          <Select
            value={leaderboardFilters?.viewID || '__all__'}
            onValueChange={(value) =>
              applyView(value === '__all__' ? '' : value)
            }
          >
            <SelectTrigger className="w-full sm:w-[180px] bg-muted border-0">
              <SelectValue placeholder="All KPIs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All KPIs</SelectItem>
              {leaderboardViews?.map((view: any) => (
                <SelectItem key={view.id} value={view.id}>
                  {view.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={leaderboardFilters?.dimensionType || 'circle'}
            onValueChange={(value) =>
              updateFilters({ dimensionType: value as DimensionType })
            }
            disabled={viewsStack.length === orgCircleLevels.length + 1}
          >
            <SelectTrigger className="w-full sm:w-[140px] bg-muted border-0">
              <SelectValue placeholder="Team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="circle">Team</SelectItem>
              <SelectItem value="person">People</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={leaderboardFilters?.dimensionTimeframe || 'M'}
            onValueChange={(value) =>
              updateFilters({
                dimensionTimeframe: value as DimensionTimeframe,
                dimensionValue: getDimensionValue(value as DimensionTimeframe),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-[140px] bg-muted border-0">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="D">Day</SelectItem>
              <SelectItem value="W">Week</SelectItem>
              <SelectItem value="M">Month</SelectItem>
              <SelectItem value="Y">Year</SelectItem>
            </SelectContent>
          </Select>

          {/* Timeframe value input */}
          {leaderboardFilters?.dimensionTimeframe === 'D' && (
            <input
              type="date"
              value={leaderboardFilters?.dimensionValue || ''}
              onChange={(e) =>
                updateFilters({ dimensionValue: e.target.value })
              }
              className="col-span-2 sm:col-span-1 h-9 px-3 rounded-md bg-muted border-0 text-sm text-foreground"
            />
          )}
          {leaderboardFilters?.dimensionTimeframe === 'W' && (
            <input
              type="week"
              value={
                leaderboardFilters?.dimensionValue
                  ? formatDateForWeekInput(leaderboardFilters.dimensionValue)
                  : ''
              }
              onChange={(e) => {
                const formattedDate = getWeekDimensionFormattedDate(
                  e.target.value
                );
                updateFilters({ dimensionValue: formattedDate });
              }}
              className="col-span-2 sm:col-span-1 h-9 px-3 rounded-md bg-muted border-0 text-sm text-foreground"
            />
          )}
          {leaderboardFilters?.dimensionTimeframe === 'M' && (
            <input
              type="month"
              value={leaderboardFilters?.dimensionValue || ''}
              onChange={(e) =>
                updateFilters({ dimensionValue: e.target.value })
              }
              className="col-span-2 sm:col-span-1 h-9 px-3 rounded-md bg-muted border-0 text-sm text-foreground"
            />
          )}
          {leaderboardFilters?.dimensionTimeframe === 'Y' && (
            <Select
              value={leaderboardFilters?.dimensionValue || ''}
              onValueChange={(value) =>
                updateFilters({ dimensionValue: value })
              }
            >
              <SelectTrigger className="w-full sm:w-[120px] bg-muted border-0">
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {generateYearOptions().map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Breadcrumb + Settings */}
        <div className="flex items-center justify-between px-6 lg:px-10 py-2">
          <div className="flex items-center gap-1">
            {viewsStack.map((view: LeaderboardStackView, index: number) => (
              <React.Fragment
                key={`${view.circleName || view.circleLevelName}-${index}`}
              >
                <button
                  onClick={() => handleStackClick(index)}
                  className={cn(
                    'text-sm font-bold transition-colors',
                    index === viewsStack.length - 1
                      ? 'text-muted-foreground cursor-default'
                      : 'text-primary hover:text-primary/80 cursor-pointer'
                  )}
                >
                  {view.circleName || view.circleLevelName}
                </button>
                {index !== viewsStack.length - 1 && (
                  <ChevronRight className="size-4 text-primary" />
                )}
              </React.Fragment>
            ))}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8">
                <Settings2 className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled
                className="text-xs text-muted-foreground"
              >
                Results per page
              </DropdownMenuItem>
              {[5, 10, 20, 50, 80, 100].map((limit) => (
                <DropdownMenuItem
                  key={limit}
                  onClick={() => handleLimitChange(limit)}
                  className={cn(
                    leaderboardFilters?.limit === limit && 'bg-accent'
                  )}
                >
                  {limit}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="px-6 lg:px-10 pb-4">
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-auto min-w-[700px]">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="text-left font-bold text-muted-foreground px-3 py-3 whitespace-nowrap w-[60px]">
                        Rank
                      </th>
                      <th className="text-left font-bold text-muted-foreground px-3 py-3 whitespace-nowrap min-w-[140px]">
                        Name
                      </th>
                      {kpiColumns.map((kpi) => (
                        <th
                          key={kpi}
                          className="text-left font-bold text-muted-foreground px-3 py-3 whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                          onClick={() => handleSort(kpi)}
                        >
                          <div className="flex items-center gap-1">
                            <span className="truncate max-w-[80px]">{kpi}</span>
                            {leaderboardFilters?.sortBy === kpi ? (
                              leaderboardFilters?.sortOrder === 'asc' ? (
                                <ArrowUp className="size-3 shrink-0" />
                              ) : (
                                <ArrowDown className="size-3 shrink-0" />
                              )
                            ) : (
                              <ArrowUpDown className="size-3 opacity-40 shrink-0" />
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboardData?.results?.map(
                      (row: Result, index: number) => (
                        <tr
                          key={row.id || index}
                          className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-3 py-3 text-foreground font-medium whitespace-nowrap">
                            {row.rank}
                          </td>
                          <td className="px-3 py-3">
                            <button
                              onClick={() => {
                                if (
                                  leaderboardFilters?.dimensionType === 'circle'
                                ) {
                                  handleCircleClick(row);
                                } else {
                                  handlePersonClick(row.id);
                                }
                              }}
                              className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer whitespace-nowrap"
                            >
                              <UserAvatar
                                imageName={row.imageName}
                                name={row.name}
                                size="sm"
                              />
                              <span className="font-semibold text-foreground text-sm">
                                {row.name}
                              </span>
                            </button>
                          </td>
                          {kpiColumns.map((kpi) => (
                            <td
                              key={kpi}
                              className="px-3 py-3 text-foreground whitespace-nowrap"
                            >
                              {row.kpis?.[kpi] ?? '0'}
                            </td>
                          ))}
                        </tr>
                      )
                    )}
                    {(!leaderboardData?.results ||
                      leaderboardData.results.length === 0) && (
                      <tr>
                        <td
                          colSpan={kpiColumns.length + 2}
                          className="px-3 py-10 text-center text-muted-foreground"
                        >
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {nextTokensList.length > 0 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => {
                    const newIndex = currentPageIndex - 1;
                    const newToken = ['', ...nextTokensList][newIndex];
                    updateFilters({ nextToken: newToken });
                  }}
                >
                  Previous
                </Button>
                {getVisiblePages().map((page, index) => {
                  if (page === '...') {
                    return (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-2 text-muted-foreground font-semibold"
                      >
                        ...
                      </span>
                    );
                  }
                  return (
                    <Button
                      key={page}
                      variant={page === currentPage ? 'default' : 'outline'}
                      size="sm"
                      className="min-w-[36px]"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => {
                    const newIndex = currentPageIndex + 1;
                    const newToken = ['', ...nextTokensList][newIndex];
                    updateFilters({ nextToken: newToken });
                  }}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
